/**
 * El dock de chats: ventanas de WhatsApp acopladas abajo, como Messenger o como las ventanas de
 * redacción de Gmail.
 *
 * POR QUÉ NO UN MODAL. Un modal es exclusivo: tapa la pantalla de atrás y obliga a cerrarlo antes
 * de hablar con el siguiente cliente. El operador que despacha treinta pedidos abre y cierra
 * treinta veces, y —peor— mientras escribe NO ve el pedido del que está hablando. El dock resuelve
 * las dos cosas: el trabajo de atrás queda a la vista, y se pueden dejar dos o tres conversaciones
 * esperando respuesta en vez de una sola bloqueante.
 *
 * POR QUÉ VIVE EN EL SHELL Y NO EN UNA PANTALLA. Si el dock fuera de Operaciones, al navegar a CRM
 * las ventanas morirían con el borrador adentro, y CRM tendría que construir el suyo. Acá arriba,
 * cualquier pantalla llama `useWaChatDock().abrir({...})` y hereda el mismo chat. Mismo criterio
 * que [WaInboxNavContext], que existe para sobrevivir a los cambios de ruta.
 *
 * 🪤 MINIMIZAR NO PUEDE DESMONTAR, Y AHÍ ESTÁ TODO EL VALOR. Si al minimizar se desmonta la
 * ventana, se pierde el borrador a medio escribir y la posición del scroll — que es exactamente lo
 * que uno guarda cuando minimiza. El dock por eso mantiene montadas TODAS las ventanas abiertas y
 * solo esconde las minimizadas con CSS. Este archivo guarda la lista; el que la respeta es
 * [WaChatDock].
 *
 * 🪤 Y POR ESO HAY UN TOPE DE TRES. Cada ventana montada es una suscripción de realtime y una lista
 * de mensajes viva. Sin tope, una tarde de trabajo deja veinte suscripciones abiertas. Al abrir la
 * cuarta, la más vieja se minimiza —no se cierra, que perdería su borrador— y queda como píldora.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/** Cuántas ventanas pueden estar EXPANDIDAS a la vez. Las demás quedan como píldora. */
export const MAX_VENTANAS_ABIERTAS = 3;

/**
 * Altura del dock en la pila de capas. Por encima de la tarjeta de llamada activa (10150) y por
 * debajo del copiloto (99990).
 *
 * 🪤 VIVE ACÁ, NO EN EL COMPONENTE, PORQUE NO ES UN NÚMERO SUELTO: ES UNA RELACIÓN. El menú de
 * chats del encabezado tiene que quedar POR ENCIMA de las ventanas —si no, la lista se abre detrás
 * de los chats y no se puede elegir—, y deriva su capa de esta. Con el número escrito a mano en dos
 * archivos, mover uno rompe el otro en silencio.
 */
export const Z_DOCK = 10200;

/**
 * Qué hay dentro de una ventana acoplada.
 *
 * 🚨 LA PRUEBA DEL AGENTE ES UNA VENTANA MÁS, NO UN DOCK APARTE. Antes vivía en su propio portal en la
 * misma esquina, así que se dibujaba ENCIMA de los chats reales en vez de al lado. El founder lo pidió
 * así: «debe comportarse como un chat más… al costado y cerrarse tipo burbuja como en Messenger».
 *
 * Y hacerlo bien no es moverla de lugar: es que la administre el MISMO dock. Así hereda gratis el
 * acomodo lateral, el tope de tres abiertas, la minimización en píldora y el no perder el borrador —
 * y no hay dos sistemas de ventanas que mantener sincronizados.
 */
export type TipoDeVentana = 'cliente' | 'prueba';

export interface ChatAcoplado {
  /** Identidad de la ventana. Para un cliente es su teléfono; para una prueba, el id del agente. */
  id: string;
  tipo: TipoDeVentana;
  telefono: string;
  nombre: string;
  /** Solo en las de prueba: por qué canal se está probando (cambia el formato de la respuesta). */
  canal?: string;
  /** Solo en las de prueba: el agente que se está probando. */
  agenteId?: string;
  /**
   * Cuántas veces se reinició esta prueba.
   *
   * 🪤 REINICIAR ES REMONTAR, no vaciar estado a mano. El contador viaja al `key` del componente, así
   * que React lo desmonta y lo vuelve a montar limpio: la conversación, el borrador y el error se van
   * juntos. Vaciarlos uno por uno es la forma de olvidarse de uno.
   */
  reinicios?: number;
  /**
   * Contexto de por qué se abrió: "#1042", "Lead", el nombre de la tienda. Se muestra junto al
   * nombre porque quien despacha identifica al cliente por su pedido, no solo por su nombre.
   */
  etiqueta?: string;
  minimizado: boolean;
}

interface DockApi {
  chats: ChatAcoplado[];
  abrir: (chat: { telefono: string; nombre: string; etiqueta?: string }) => void;
  /** Abre (o trae al frente) la ventana de prueba de un agente. */
  abrirPrueba: (p: { agenteId: string; nombre: string; canal: string }) => void;
  /** Vacía la conversación de prueba sin cerrar la ventana. */
  reiniciarPrueba: (id: string) => void;
  cerrar: (id: string) => void;
  alternarMinimizado: (id: string) => void;
}

const Ctx = createContext<DockApi | null>(null);

/** Los últimos 9 dígitos son la identidad real del cliente en Perú, sin prefijo ni espacios. */
function idDeTelefono(telefono: string): string {
  return telefono.replace(/\D/g, '').slice(-9);
}

export function WaChatDockProvider({ children }: { children: ReactNode }) {
  const [chats, setChats] = useState<ChatAcoplado[]>([]);

  const abrir = useCallback((chat: { telefono: string; nombre: string; etiqueta?: string }) => {
    const id = idDeTelefono(chat.telefono);
    if (!id) return;

    setChats((prev) => {
      const yaEsta = prev.find((c) => c.id === id);
      // Reabrir un chat que ya está: se expande y se trae al frente, no se duplica.
      if (yaEsta) {
        return [...prev.filter((c) => c.id !== id), { ...yaEsta, minimizado: false, etiqueta: chat.etiqueta ?? yaEsta.etiqueta }];
      }

      const nuevo: ChatAcoplado = { id, tipo: 'cliente', telefono: chat.telefono, nombre: chat.nombre, etiqueta: chat.etiqueta, minimizado: false };
      const conElNuevo = [...prev, nuevo];

      // Tope de expandidas: la más vieja se MINIMIZA (conserva su borrador), no se cierra.
      const expandidas = conElNuevo.filter((c) => !c.minimizado);
      if (expandidas.length > MAX_VENTANAS_ABIERTAS) {
        const aMinimizar = expandidas[0].id;
        return conElNuevo.map((c) => (c.id === aMinimizar ? { ...c, minimizado: true } : c));
      }
      return conElNuevo;
    });
  }, []);

  /**
   * Abre la ventana de prueba del agente. Misma mecánica que un chat: si ya está, se expande y se
   * trae al frente en vez de duplicarse.
   *
   * 🪤 EL ID LLEVA PREFIJO `prueba:` PARA QUE NO PUEDA CHOCAR con el de un cliente. Sin eso, un agente
   * cuyo id se pareciera a un teléfono normalizado reabriría el chat de una persona real.
   */
  const abrirPrueba = useCallback((p: { agenteId: string; nombre: string; canal: string }) => {
    const id = `prueba:${p.agenteId}`;
    setChats((prev) => {
      const yaEsta = prev.find((c) => c.id === id);
      if (yaEsta) {
        return [...prev.filter((c) => c.id !== id), { ...yaEsta, minimizado: false, canal: p.canal }];
      }
      const nuevo: ChatAcoplado = {
        id, tipo: 'prueba', telefono: '', nombre: p.nombre,
        canal: p.canal, agenteId: p.agenteId, minimizado: false,
      };
      const conElNuevo = [...prev, nuevo];
      const expandidas = conElNuevo.filter((c) => !c.minimizado);
      if (expandidas.length > MAX_VENTANAS_ABIERTAS) {
        const aMinimizar = expandidas[0].id;
        return conElNuevo.map((c) => (c.id === aMinimizar ? { ...c, minimizado: true } : c));
      }
      return conElNuevo;
    });
  }, []);

  const reiniciarPrueba = useCallback((id: string) => {
    setChats((prev) => prev.map((c) => (c.id === id ? { ...c, reinicios: (c.reinicios ?? 0) + 1 } : c)));
  }, []);

  const cerrar = useCallback((id: string) => {
    setChats((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const alternarMinimizado = useCallback((id: string) => {
    setChats((prev) => {
      const objetivo = prev.find((c) => c.id === id);
      if (!objetivo) return prev;

      // Expandir una píldora también cuenta contra el tope.
      if (objetivo.minimizado) {
        const expandidas = prev.filter((c) => !c.minimizado);
        const desplazado = expandidas.length >= MAX_VENTANAS_ABIERTAS ? expandidas[0].id : null;
        return prev.map((c) => {
          if (c.id === id) return { ...c, minimizado: false };
          if (c.id === desplazado) return { ...c, minimizado: true };
          return c;
        });
      }
      return prev.map((c) => (c.id === id ? { ...c, minimizado: true } : c));
    });
  }, []);

  const api = useMemo(() => ({ chats, abrir, abrirPrueba, reiniciarPrueba, cerrar, alternarMinimizado }), [chats, abrir, abrirPrueba, reiniciarPrueba, cerrar, alternarMinimizado]);
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

/**
 * 🪤 DEVUELVE UN API INERTE SI NO HAY PROVIDER, EN VEZ DE REVENTAR. El dock cuelga del shell del
 * admin, y hay pantallas que viven fuera de él (el editor de temas, el login). Que una lista se
 * caiga entera porque el botón de WhatsApp no encontró su contexto sería un intercambio pésimo:
 * ahí el botón simplemente no hace nada.
 */
export function useWaChatDock(): DockApi {
  return useContext(Ctx) ?? { chats: [], abrir: () => {}, abrirPrueba: () => {}, reiniciarPrueba: () => {}, cerrar: () => {}, alternarMinimizado: () => {} };
}
